import { FormInputBlock } from './form-input-block.js'
import type { EventType, HtmlType, NameAndType } from '../../types.js'
import type { TsCodeType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { BoReference } from '../../bos/bo-reference.js'
import { Parameter } from '../../script/parameter.js'
import {
	EditorBoRefDetailProperty,
	ScreenEditorProperty,
} from '../decorators/editor-property-types.js'
import { Screen } from '../../bos/screen.js'
import { BlockDataSourceDefinition } from '../common/block-data-source-definition.js'
import { Expression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { BlockWithDataSource } from '../common/block-with-data-source.js'

export const SearchableDropdownBindingModes = {
	object: 'Whole object that was selected',
	id: 'Id of the object that was selected',
} as const
export type SearchableDropdownBindingMode =
	keyof typeof SearchableDropdownBindingModes

@jsonClass('SearchableDropdownBlock')
export class SearchableDropdownBlock
	extends FormInputBlock
	implements BlockWithDataSource
{
	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 1,
		label: 'Label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
	})
	labelCode = new TextOrExpression_assumeExpression('', 'T')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 2,
		label: 'Binding mode',
		options: Object.entries(SearchableDropdownBindingModes),
	})
	bindingMode: SearchableDropdownBindingMode = 'object'

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Binding',
		codeLanguage: 'ts',
		tsReturnType: () => 'string | number',
		editorSize: 'singleline',
		isBinding: true,
		isMandatory: () => true,
	})
	bindingCode = new Expression()

	@jsonProperty(BlockDataSourceDefinition)
	@ScreenEditorInput<SearchableDropdownBlock>({
		order: 3,
		label: '',
		compositionObj: (blocks) => blocks[0].dataSource,
	})
	dataSource: BlockDataSourceDefinition = new BlockDataSourceDefinition()

	// @jsonProperty()
	// @ScreenEditorInput({
	// 	inputType: 'boRef',
	// 	order: 3,
	// 	label: 'Entity',
	// 	allowedBoTypes: ['Entity'],
	// 	includeImportedModules: true,
	// 	isMandatory: () => true,
	// })
	// entityRef: BoReference = new BoReference()

	// @jsonProperty()
	// @ScreenEditorInput<SearchableDropdownBlock>({
	// 	inputType: 'boRefDetail',
	// 	order: 4,
	// 	label: 'Entity subset',
	// 	boRef: blocks => blocks[0].entityRef,
	// 	isMandatory: () => true,
	// })
	// entitySubsetName: string = ''

	// @jsonProperty()
	// @ScreenEditorInput({
	// 	inputType: 'boRef',
	// 	order: 4,
	// 	label: 'ServerDataStore',
	// 	allowedBoTypes: ['ServerDataStore'],
	// 	includeImportedModules: true,
	// 	isMandatory: () => true,
	// })
	// serverDataStoreRef: BoReference = new BoReference()

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 5,
		label: 'Minimum length before searching',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	minSearchLengthCode = new Expression('3')

	// @jsonProperty(String)
	// @ScreenEditorInput({
	// 	inputType: 'code',
	// 	order: 6,
	// 	label: 'Maximum number of results',
	// 	codeLanguage: 'ts',
	// 	tsReturnType: () => 'number',
	// 	editorSize: 'singleline',
	// 	isBinding: false,
	// 	isMandatory: () => true,
	// })
	// maximumResultsCode?: TsCodeType

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 7,
		label: 'Selected option label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string | number',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
		additionalVariables: (blocks: SearchableDropdownBlock[]) => ({
			$option: blocks[0].dataSource.entityRef.isFullyDefined()
				? blocks[0].dataSource.entityRef.getQualifiedName(
						blocks[0].dataSource.entitySubsetName,
					)
				: 'any',
			$index: 'number',
		}),
		tooltip: () => ({
			text: 'Use <code>$option</code> to access each item of the options list and <code>$index</code> to access the zero-based index of the current item',
			acceptHtml: true,
		}),
	})
	selectedOptionLabelCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 7,
		label: 'Option label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string | number',
		editorSize: 'singleline',
		isBinding: false,
		additionalVariables: (blocks: SearchableDropdownBlock[]) => ({
			$option: blocks[0].dataSource.entityRef.isFullyDefined()
				? blocks[0].dataSource.entityRef.getQualifiedName(
						blocks[0].dataSource.entitySubsetName,
					)
				: 'any',
			$index: 'number',
		}),
		tooltip: () => ({
			text: 'Use this (or a Content Area) to display an alternative label for options in the dropdown. If empty, the "Selected option label" will be used.<br><br>Use <code>$option</code> to access each item of the options list and <code>$index</code> to access the zero-based index of the current item',
			acceptHtml: true,
		}),
		alternativeContentAreaName: 'optionLabel',
	})
	optionLabelCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 3,
		label: () => 'Empty Selection',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		tsReturnType: () => '[any, string, boolean] | undefined',
		isBinding: false,
		isMandatory: () => false,
		tooltip: () => ({
			text: "Configuration tuple: [value, label, validation] — defines the option's bound value, display label, and validation rules.\nLeave blank to disable this option.",
		}),
	})
	emptySelection = new Expression()

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'code',
		order: 7,
		label: 'Debouncing delay (ms)',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
		tooltip: () => ({
			text: 'Delay in milliseconds that the app will wait after keyboard inputs until the search is executed; this helps avoid sending a request after every single typed character',
		}),
	})
	debouncingDelayCode?: TsCodeType = '250'

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'code',
		order: 8,
		label: 'Additional find options',
		codeLanguage: 'ts',
		tsReturnType: (blocks: SearchableDropdownBlock[]) =>
			`Sys.Types.FindManyOptions<${blocks[0]?.dataSource.entityRef.isFullyDefined() ? blocks[0]?.dataSource.entityRef.getQualifiedName(blocks[0].dataSource.entitySubsetName) : 'any'}> | null`,
		editorSize: 'normal',
		isBinding: false,
		tooltip: () => ({
			text: 'Provide additional find options if desired, e.g. to define sorting or additional filtering',
		}),
	})
	findOptionsCode?: TsCodeType = 'null'

	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 10,
		label: 'Allow clearing value',
	})
	@jsonProperty(Boolean)
	allowClearing: boolean = true

	canAcceptVariableString = (variable: NameAndType) => !!variable.isArray
	acceptVariableString = (variable: NameAndType, screen: Screen) => {
		this.dataSource.acceptVariableString(variable, screen)
	}

	get allowedEventTypes(): EventType[] {
		return [
			{
				name: 'optionSelected',
				parameters: [
					new Parameter({
						name: '$option',
						type: `${this.dataSource.itemTsType || this.dataSource.entityRef.getQualifiedName() || 'any'} | null`,
						defaultExpression: '$event',
					}),
				],
			},
		]
	}

	getValidationValueType(): string {
		return `${this.dataSource.getItemTsType() || 'any'} | null`
	}

	producePreview(): HtmlType {
		return `<select tabindex="-1"><option>${this.exampleText || 'Option'}</option></select>`
	}

	getEditorCategory(): string {
		return 'Additional Form Fields'
	}
}
